import { Injectable, inject } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ConversionValidationCacheService } from '../mbrs/conversion-validation-cache.service';
import { RuntimeConfigService } from '../common/runtime-config.service';
import { ConversionProgressPayload } from '../../../types/commons';

/**
 * Thin wrapper around the `/conversion-progress` SignalR hub.
 *
 * Hub lifetime is owned by `MainLayoutComponent` via `startConnection()` /
 * `stopConnection()`. Per-event handlers (`Receive1st/2ndConversionProgress`)
 * are registered exactly once inside `startConnection()` and torn down inside
 * `stopConnection()` — consumers subscribe to `progress1st$` / `progress2nd$`
 * with `takeUntilDestroyed(destroyRef)` instead of registering their own
 * callbacks. This removes the previous foot-gun where each `listenFor1st…` /
 * `listenFor2nd…` call appended a new handler to the hub (SignalR `.on()`
 * does not replace), stacking callbacks across navigations and causing the
 * UI to react multiple times to a single progress ping.
 */
@Injectable({
  providedIn: 'root',
})
export class ConversionProgressService {
  private readonly config = inject(RuntimeConfigService);
  private readonly ApiUrlHub = `${this.config.apiUrlHub()}/conversion-progress`;
  private hubConnection!: signalR.HubConnection;
  public messageReceived = new Subject<{ user: string; message: string }>();
  private validationCache = inject(ConversionValidationCacheService);

  private readonly progress1stSubject = new Subject<ConversionProgressPayload>();
  private readonly progress2ndSubject = new Subject<ConversionProgressPayload>();

  /** Phase-1 progress events (`Receive1stConversionProgress`). */
  readonly progress1st$: Observable<ConversionProgressPayload> = this.progress1stSubject.asObservable();

  /** Phase-2 progress events (`Receive2ndConversionProgress`). */
  readonly progress2nd$: Observable<ConversionProgressPayload> = this.progress2ndSubject.asObservable();

  private static readonly CONVERSION_PHASE_1 = 'Receive1stConversionProgress';
  private static readonly CONVERSION_PHASE_2 = 'Receive2ndConversionProgress';

  constructor() {}

  /**
   * Returns the underlying hub connection, or null if `startConnection()`
   * has not run yet. The connection is created lazily by the conversion
   * flow, so callers that render before the first navigation to a
   * conversion-aware page (e.g. the dev SignalR Console) must tolerate
   * null and avoid reaching into private state.
   */
  public getHubConnection(): signalR.HubConnection | null {
    return this.hubConnection ?? null;
  }

  public startConnection = () => {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.ApiUrlHub, {
        withCredentials: true,
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    // Lifecycle hooks for reconnect/close diagnostics (dev/staging only)
    this.hubConnection.onreconnecting((err) => {
      // ----------------------
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // if (!environment.production) {
      //   console.warn('[ConversionProgressService] Reconnecting...', {
      //     hubUrl: this.ApiUrlHub,
      //     state: this.hubConnection.state,
      //     error: err?.message,
      //     timestamp: new Date().toISOString(),
      //   });
      // }
      // ----------------------
    });

    this.hubConnection.onreconnected((connectionId) => {
      // ----------------------
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // if (!environment.production) {
      //   console.log('[ConversionProgressService] Reconnected', {
      //     hubUrl: this.ApiUrlHub,
      //     connectionId,
      //     state: this.hubConnection.state,
      //     timestamp: new Date().toISOString(),
      //   });
      // }
      // ----------------------
    });

    this.hubConnection.onclose((err) => {
      // ----------------------
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // if (!environment.production) {
      //   console.warn('[ConversionProgressService] Connection closed', {
      //     hubUrl: this.ApiUrlHub,
      //     state: this.hubConnection.state,
      //     error: err?.message,
      //     timestamp: new Date().toISOString(),
      //   });
      // }
      // ----------------------
    });

    // Register hub handlers exactly once per connection. Consumers receive
    // payloads through `progress1st$` / `progress2nd$`; nothing else should
    // call `hubConnection.on(CONVERSION_PHASE_1/CONVERSION_PHASE_2, ...)` anywhere in the app.
    this.hubConnection.on(
      ConversionProgressService.CONVERSION_PHASE_1,
      (data: ConversionProgressPayload) => {
        this.logProgressEvent(ConversionProgressService.CONVERSION_PHASE_1, data);
        this.invalidateCacheForProgressEvent(data);
        this.progress1stSubject.next(data);
      }
    );

    this.hubConnection.on(
      ConversionProgressService.CONVERSION_PHASE_2,
      (data: ConversionProgressPayload) => {
        this.logProgressEvent(ConversionProgressService.CONVERSION_PHASE_2, data);
        this.invalidateCacheForProgressEvent(data);
        this.progress2ndSubject.next(data);
      }
    );

    this.hubConnection
      .start()
      .then(() => {
        // ----------------------
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // if (!environment.production) {
        //   console.log('[ConversionProgressService] Connection started', {
        //     hubUrl: this.ApiUrlHub,
        //     connectionId: this.hubConnection.connectionId,
        //     state: this.hubConnection.state,
        //     transport: 'WebSockets',
        //     timestamp: new Date().toISOString(),
        //   });
        // }
        // ----------------------
      })
      .catch((err) => {
        console.error('[ConversionProgressService] Error while starting connection', {
          hubUrl: this.ApiUrlHub,
          state: this.hubConnection.state,
          error: err?.message ?? err,
          stack: err?.stack,
          timestamp: new Date().toISOString(),
        });
      });
  };

  public stopConnection = () => {
    const connectionIdBeforeStop = this.hubConnection.connectionId;
    // Deregister hub handlers symmetrically so a subsequent startConnection()
    // does not end up with duplicates. `.off(name)` removes every handler
    // bound to that event on this connection.
    this.hubConnection.off(ConversionProgressService.CONVERSION_PHASE_1);
    this.hubConnection.off(ConversionProgressService.CONVERSION_PHASE_2);
    this.hubConnection.stop().then(() => {
      // ----------------------
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // if (!environment.production) {
      //   console.log('[ConversionProgressService] Connection stopped', {
      //     hubUrl: this.ApiUrlHub,
      //     connectionId: connectionIdBeforeStop,
      //     state: this.hubConnection.state,
      //     timestamp: new Date().toISOString(),
      //   });
      // }
      // ----------------------
    });
  };

  /**
   * Invalidate the cached validation statuses for the conversion targeted by
   * a SignalR progress event. The backend's `data.status` payload is not
   * authoritative (currently hardcoded — the real status is computed by the
   * frontend validations), so the cache is dropped here to force the next
   * listing/dashboard read to re-validate against the freshest data.
   *
   * Called for every progress event regardless of new status: an InProgress
   * conversion has no cache entry to begin with (we skip validation for that
   * status), so the call degenerates to a no-op in that case.
   */
  private invalidateCacheForProgressEvent(data: ConversionProgressPayload | undefined): void {
    const conversionId = data?.conversionId;

    if (typeof conversionId === 'string' && conversionId.length > 0)
      this.validationCache.invalidate(conversionId);
  }

  /**
   * Diagnostic log for incoming SignalR progress events. Surfaces the raw
   * payload so we can confirm which event name fired, what `status` the
   * backend reported, and the `phase` field that drives phase-aware UI
   * branching. Helps triage reports of conversions appearing in the wrong
   * phase after a fresh submit.
   *
   * Gated by `!environment.production` to match the rest of the
   * lifecycle/reconnect diagnostics in this file. Flip the gate off
   * temporarily if the issue needs to be reproduced in a production build.
   */
  private logProgressEvent(event: string, data: ConversionProgressPayload | undefined): void {
    if (environment.production)
      return;
    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`[ConversionProgressService] ${event} received`, {
    //   event,
    //   conversionId: data?.conversionId,
    //   status: data?.status,
    //   phase: data?.phase,
    //   companyName: data?.companyName,
    //   fsFileName: data?.fsFileName,
    //   hubConnectionId: this.hubConnection?.connectionId,
    //   hubState: this.hubConnection?.state,
    //   timestamp: new Date().toISOString(),
    //   rawPayload: data,
    // });
    // ----------------------
  }
}
